import type { HydratedDocument } from 'mongoose';
import { z } from 'zod';
import { DateSchema, ObjectIdSchema } from './base';

export enum TOSVersions {
  NONE = 'NONE',
  '2019-10' = '2019-10',
}

export enum BillableItemTypes {
  BASE_PRICE = 'basePrice',
  SAFEMAP_BASE_PRICE = 'SafemapBasePrice',
  ACTIVATE_EVENT = 'activateEvent',
  SPONSOR = 'sponsor',
  KEY = 'key',
  TIMING = 'timing',
  MONITOR = 'monitor',
  HIGH_RESOLUTION = 'highResolution',
  DATA_FEED = 'dataFeed',
  STARTER = 'starter',
  GPS_DEVICE = 'gpsDevice',
  TRANSPONDER = 'transponder',
  PAGE_VIEW = 'pageView',
  SIM_CARD = 'simCard',
  APP = 'app',
  TRACKER_MANAGEMENT = 'trackerManagement',
  SMS = 'sms',
  EVENT_CYCLE = 'eventCycle',
  EVENT_DAY = 'eventDay',
  DATA_USAGE_ZONE_1 = 'dataUsageZone1',
  DATA_USAGE_ZONE_1_V6_SIM = 'dataUsageZone1V6Sim',
  DATA_USAGE_ZONE_2 = 'dataUsageZone2',
  DATA_USAGE_ZONE_2_V6_SIM = 'dataUsageZone2V6Sim',
  DATA_USAGE_ZONE_3 = 'dataUsageZone3',
  DATA_USAGE_ZONE_3_V6_SIM = 'dataUsageZone3V6Sim',

  // deprecated
  EXTEND_EVENT = 'extendEvent',
}

const UserStatisticsSchema = z.object({
  numberCreatedEvents: z.number().default(0),
  numberPaidEvents: z.number().default(0),
});

const IntegrationsSchema = z.object({
  stripeId: z.string().optional(),
  mailchimpSubscribed: z.boolean().default(false),
  mailchimpTags: z.array(z.string()).default([]),
  raceResultCustomerId: z.string().nullable(),
  raceResultApiKey: z.string().nullable(),
  insidersAccessToken: z.string().nullable(),
  insidersRefreshToken: z.string().nullable(),
  insidersExpiresAt: DateSchema.nullable(),
});

const MarketingCampaignSchema = z.object({
  eventId: ObjectIdSchema,
  taggedAt: DateSchema,
});

export const MarketingSchema = z.object({
  firstUsefulTest: MarketingCampaignSchema.extend({
    followUpTaggedAt: DateSchema.optional(),
  }).optional(),
  reactivation: MarketingCampaignSchema.optional(),
});

const AddressSchema = z.object({
  city: z.string().nullable(),
  country: z.string().nullable(),
  line1: z.string().nullable(),
  line2: z.string().nullable(),
  postal_code: z.string().nullable(),
  state: z.string().nullable(),
});

const TaxIdSchema = z.object({
  id: z.string(),
  object: z.literal('tax_id'),
  country: z.string().nullable(),
  created: z.number(),
  customer: z.string().nullable(),
  deleted: z.undefined(),
  livemode: z.boolean(),
  type: z.string(),
  value: z.string(),
  verification: z.object({
    status: z.enum(['pending', 'verified', 'unverified', 'unavailable']),
    verified_address: z.string().nullable(),
    verified_name: z.string().nullable(),
  }),
});

const ShippingSchema = z.object({
  address: AddressSchema.optional(),
  carrier: z.string().nullish(),
  name: z.string().optional(),
  phone: z.string().nullish(),
  tracking_number: z.string().nullish(),
});

const CardSchema = z.object({
  address_line1_check: z.string().nullish(),
  address_zip_check: z.string().nullish(),
  brand: z.string().nullish(),
  country: z.string().nullish(),
  cvc_check: z.string().nullish(),
  description: z.string().optional(),
  dynamic_last4: z.string().nullish(),
  exp_month: z.number().nullish(),
  exp_year: z.number().nullish(),
  fingerprint: z.string().optional(),
  funding: z.string().nullish(),
  iin: z.string().optional(),
  issuer: z.string().optional(),
  last4: z.string().nullish(),
  name: z.string().nullish(),
  three_d_secure: z.string().optional(),
  tokenization_method: z.string().nullish(),
});

export const UserSchema = z.object({
  id: ObjectIdSchema,
  admin: z.boolean(),
  isBetaUser: z.boolean(),
  receivedAt: DateSchema.optional(),
  updatedAt: DateSchema,
  createdAt: DateSchema,
  isConfirmed: z.boolean(),
  confirmedAt: DateSchema.optional(),
  encodedPass: z.string().optional(),
  saltToken: z.string().optional(),
  payments: z
    .object({
      lastUsageSync: DateSchema,
    })
    .optional(),
  name: z.string().optional(),
  company: z.string().optional(),
  email: z.string(),
  phoneNumber: z.string().optional(),
  country: z.string().optional(),
  useTimingAPI: z.boolean().optional(),
  isReseller: z.boolean(),
  password: z.string().optional(),
  impersonated: z.boolean().optional(), // Set on GET /api/users/current only, when an admin impersonates this user. Derived per request, never stored.
  acceptedTOSVersion: z.enum(TOSVersions),
  apiToken: z.string().nullable(),
  stats: UserStatisticsSchema.optional(),
  parentId: ObjectIdSchema.nullable(),
  integrations: IntegrationsSchema,
  subscriptions: z.object({
    [BillableItemTypes.APP]: z.number(),
  }),
  checkout: z
    .object({
      address: AddressSchema,
      taxIds: z.array(TaxIdSchema),
      shipping: ShippingSchema.nullish(),
      defaultCard: CardSchema.nullable(),
    })
    .optional(),
  createdByTest: z.boolean().default(false),
  promotions: z.object({
    december2024: z.boolean().optional(),
  }),
  trackingConsent: z
    .object({
      analytics: z.boolean(),
      updatedAt: DateSchema,
    })
    .optional(),
  // Admins only.
  marketing: MarketingSchema.optional(),
});

const AdminUserSchema = UserSchema.extend({
  admin: z.literal(true),
});
const ChildUserSchema = UserSchema.extend({
  parentId: ObjectIdSchema,
});

export type User = z.infer<typeof UserSchema>;
export type Admin = z.infer<typeof AdminUserSchema>;
export type ChildUser = z.infer<typeof ChildUserSchema>;

export const UserOverviewSchema = z.object({
  id: ObjectIdSchema,
  name: z.string().optional(),
  email: z.string(),
  admin: z.boolean().optional(),
  isReseller: z.boolean().optional(),
  isConfirmed: z.boolean().optional(),
  createdByTest: z.boolean().optional(),
  createdAt: DateSchema.optional(),
  stats: UserStatisticsSchema.optional(),
  parentId: ObjectIdSchema.nullable(),
  // Deep link to the contact in the Mailchimp admin. Built server-side. Admins only.
  mailchimpContactUrl: z.string().optional(),
  // Admins only.
  marketing: MarketingSchema.optional(),
  integrations: z
    .object({
      stripeId: z.string().optional(),
      // Deep link to the customer in the Stripe dashboard. Built server-side, so it points at the
      // test or live dashboard according to the API key gears-api runs. Admins only.
      stripeCustomerUrl: z.string().optional(),
      insidersAccessToken: z.string().nullish(),
      insidersRefreshToken: z.string().nullish(),
      insidersExpiresAt: DateSchema.nullish(),
    })
    .optional(),
});

export type UserOverview = z.infer<typeof UserOverviewSchema>;

const UserDocumentSchemaBase = UserSchema.omit({ id: true }).extend({
  _id: ObjectIdSchema,
  testPassword: z.function({ input: [z.string()], output: z.promise(z.boolean()) }),
  getStripeEmail: z.function({ output: z.string() }),
  addStripeId: z.function({
    input: [z.object({ phone: z.string().optional(), country: z.string().optional() }).optional()],
    output: z.promise(z.void()),
  }),
  createdByTest: z.boolean().optional(),
});

export const UserDocumentSchema = UserDocumentSchemaBase.extend({
  canRead: z.function({
    input: [UserDocumentSchemaBase.nullish()],
    output: z.promise(z.boolean()),
  }),
  canEdit: z.function({
    input: [UserDocumentSchemaBase.nullish()],
    output: z.promise(z.boolean()),
  }),
});

export interface UserDocumentMethods {
  canEdit(user: UserDocument | Express.User | null | undefined): Promise<boolean>;
  canRead(user: UserDocument | Express.User | null | undefined): Promise<boolean>;
  canDelete(user: UserDocument | Express.User | null | undefined): Promise<boolean>;
  testPassword(password: string): Promise<boolean>;
  getStripeEmail(): string;
  addStripeId(args?: { phone?: string; country?: string; company?: string }): Promise<void>;
  saveCredentials(password: string): Promise<void>;
  closeAllSessions(): Promise<void>;
  addMailchimpTags(tags: Array<string>): Promise<void>;
  removeMailchimpTags(tags: Array<string>): Promise<void>;
  updateMailchimpMergeFields(
    mergeFields: Partial<
      Record<
        | 'FNAME'
        | 'LNAME'
        | 'COUNTRY'
        | 'COMPANY'
        | 'PHONE'
        | 'FUTEVENTID'
        | 'FUVENTNAME'
        | 'FUTKEY'
        | 'FPVENTNAME'
        | 'FPAEVENTID',
        string
      >
    >,
  ): Promise<void>;
}

export type UserDocument = Omit<
  HydratedDocument<User, UserDocumentMethods>,
  'id' | 'integrations'
> & {
  integrations: Omit<User['integrations'], 'mailchimpSubscribed' | 'mailchimpTags'>;
};
